import React from 'react';
import {useParams} from 'react-router-dom';
import {Box, Grid, Typography, CardContent, Chip, Stack, CardHeader, Card} from '@mui/material';
import {SmartLink, formatDurationAbbr, formatRFC7231} from '../abelcommon';
import QueryProcessor from '../components/QueryProcessor';
import {useTx} from '../queries';
import {TabbedMainCard} from '../components/MainCard';
import {JSONTextField} from '../components/JSONTextField';
import {RichList, RichListItem} from '../components/RichList';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {latestBlockState} from "../state";
import {useRecoilValue} from 'recoil';
import {InputItem, OutputsItem} from '../components/TransactionItem'
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

function useTxWithPrecheck(txid?: string) {
    if (!txid || !txid.startsWith('0x') || txid.length !== 66) {
        throw new Error('Illegal txid.');
    }
    return useTx(txid);
}

function TxOverview({d}: { d: any }) {

    const latestBlock = useRecoilValue(latestBlockState);

    const ts = new Date();
    const dts = d.time * 1000;

    return (
        <RichList sx={{width: 1, mt: -2}}>
            <RichListItem
                title={'Transaction Hash'}
                help={
                    'a unique identifier, similar to a receipt, that serves as proof that a transaction was validated and added to the blockchain. In many cases, a transaction hash is needed in order to locate funds.'
                }
            >
                <Box>
                    <Typography variant={'body2'} sx={{wordWrap: 'anywhere'}}>0x{d.txid}</Typography>
                </Box>
            </RichListItem>

            <RichListItem title={'Status'} help={'The status of the transaction.'}>
                <Chip icon={<CheckCircleIcon/>} label={'Success'} color={'primary'} size={'small'}/>
            </RichListItem>

            <RichListItem
                title={'Block Height'}
                help={
                    'The position of the block containing this transaction in blockchain, also known as the block number.'
                }
            >
                <Typography variant={'body1'}>
                    <SmartLink to={`/block/0x${d.blockhash}`}>{d.blockheight}</SmartLink>
                </Typography>

                {latestBlock.height !== -1 && (
                    <Typography component={'span'} variant={'body2'} sx={{pr: 1}}>
                        {` (${latestBlock.height - d.blockheight} confirmations)`}
                    </Typography>
                )}
            </RichListItem>

            <RichListItem title={'Block Hash'}
                          help={'The hash of the block header of the block containing this transaction.'}>
                <Typography variant={'body2'} sx={{wordWrap: 'anywhere'}}>
                    <SmartLink to={`/block/0x${d.blockhash}`}>0x{d.blockhash}</SmartLink>
                </Typography>
            </RichListItem>

            <RichListItem title={'Timestamp'} help={'The date and time at which this transaction was confirmed.'}>
                <Stack direction={'row'} sx={{alignItems: 'center'}} spacing={0.5}>
                    <AccessTimeIcon sx={{fontSize: 14, color: 'text.secondary'}}/>
                    <Typography>{`${formatDurationAbbr(dts, ts)} ago (${formatRFC7231(dts).substring(5)})`}</Typography>
                </Stack>
            </RichListItem>

            <RichListItem title={'Type'} help={'The type of the transaction (coinbase or transfer).'}>
                {d.txtype}
            </RichListItem>

            <RichListItem title={'From'}
                          help={'The number of referenced previous transaction outputs by this transaction.'}>
                {d.txtype === 'coinbase' ? (
                    <Typography variant={'body2'} fontFamily={'monospace'}>
                        0x0 (Null Address)
                    </Typography>
                ) : (
                    <Typography variant={'body2'} fontFamily={'monospace'}>
                        {`${d.vin.length} Inputs`}
                    </Typography>
                    /*        <Stack direction={'column'} sx={{ alignItems: 'left' }} spacing={0.5}>
                              {d.vinoutpointring.map((outpointring: any, idx: number) => (
                                <Typography key={idx} variant={'body2'} fontFamily={'monospace'}>
                                  <SmartLink to={`/address/0xabe1${fingerprint}`}>0x{addressPrefixFromFingerprint(fingerprint)}...</SmartLink>
                                </Typography>
                              ))}
                            </Stack>*/
                )}
            </RichListItem>

            <RichListItem title={'To'} help={'The number of generated transaction outputs which known as UTXO.'}>
                <Typography variant={'body2'} fontFamily={'monospace'}>
                    {`${d.vout.length} Outputs`}
                </Typography>
                {/*<Stack direction={'column'} sx={{ alignItems: 'left' }} spacing={0.5}>*/}
                {/*  {VoutFingerprints.map((fingerprint: any, idx: number) => (*/}
                {/*    <Typography key={idx} variant={'body2'} fontFamily={'monospace'}>*/}
                {/*      <SmartLink to={`/address/0xabe1${fingerprint}`}>0x{addressPrefixFromFingerprint(fingerprint)}...</SmartLink>*/}
                {/*    </Typography>*/}
                {/*  ))}*/}
                {/*</Stack>*/}
            </RichListItem>

            <RichListItem
                title={'Value'}
                help={
                    'For coinbase transaction, the value is the total reward of the containing block. For transfer transaction, the value is the transaction fee.'
                }
            >
                {d.fee} ABEL
            </RichListItem>
        </RichList>
    );
}

export function ViewTx() {
    let {txid} = useParams();
    if (txid && !txid.startsWith('0x')) {
        txid = '0x' + txid;
    }

    return (
        <QueryProcessor
            query={useTxWithPrecheck(txid)}
            dataValidator={(data: any) => data.type === 'txdetail'}
            renderer={(data: any) => (
                <Grid container spacing={2}>
                    <Grid item xs={12} md={12}>
                        <Typography component={'span'} variant={'h2'}>
                            Transaction Details
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <TabbedMainCard labels={['Overview', 'Raw Data']}>
                            <CardContent>
                                <TxOverview d={data.payload}/>
                            </CardContent>
                            <CardContent>
                                <JSONTextField value={data}/>
                            </CardContent>
                        </TabbedMainCard>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <Card sx={{mt: 2, minHeight: 400}}>
                            <CardHeader
                                title={<Typography variant={'h5'} textAlign={'center'}>{`Inputs`}</Typography>}
                                subheader={
                                    <Stack sx={{px: 2, pt: 1}}>
                                        <Typography variant="inherit">
                                            Each transaction input references a previous transaction output (TXO) within
                                            a grouped set known as Ring.
                                            In the ring, every TXO corresponds to a unique cryptographic identifier
                                            (known as Serial Number).
                                        </Typography>
                                        <Typography>
                                            This mechanism enables deterministic verification of its consumption status
                                            while preserving referential integrity within the blockchain's transaction
                                            graph
                                        </Typography>
                                    </Stack>
                                }
                                sx={{p: 0}}
                            >
                            </CardHeader>
                            <CardContent>
                                {data.payload.vin.map((input: any, i: any) => (
                                    <InputItem key={`${txid}_input_${i}`} ringid={input.ringid}
                                               outpoints={input.outpoints}
                                               serialnumber={input.serialnumber}
                                               sx={{my: 2}}
                                    />
                                ))}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={1}>
                        <Stack sx={{height: '100%', alignItems: 'center', justifyContent: 'center'}}>
                            <KeyboardArrowRightIcon fontSize={"large"} sx={{
                                display: {
                                    xs: 'none',
                                    md: 'block'
                                }
                            }}/>
                            <KeyboardArrowDownIcon fontSize={"large"} sx={{
                                display: {
                                    xs: 'block',
                                    md: 'none'
                                }
                            }}/>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card sx={{mt: 2, minHeight: 400}}>
                            <CardHeader
                                title={<Typography variant={'h5'} textAlign={'center'}>{`Outputs`}</Typography>}
                                subheader={
                                    <Stack sx={{px: 2, pt: 1}}>
                                        <Typography>
                                            Each output contains information for sending ABELs, typically, an address as
                                            the recipient for the funds being sent in a transaction, and the amount of
                                            ABELs.
                                        </Typography>
                                        <Typography>
                                            When using a fully-private address, the amount of ABEL would be encrypted to
                                            prevent unauthorized parties from viewing,
                                            and only the intended recipient can known the exact amount.
                                        </Typography>
                                    </Stack>
                                }
                                sx={{p: 0}}
                            />
                            <CardContent>
                                <OutputsItem outputs={data.payload.vout}
                                             sx={{my: 2}}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}
        />
    );
}

export default ViewTx;
